/**
 * Client rates — wire types + fetch/mutation helpers + the pure
 * presentation logic behind the Pre-billing Rates card (0132, B1-D3).
 *
 * MONEY IS A STRING. The server sends numeric(12,2) as text
 * ("185.00"); every helper here works on that string — parseFloat
 * never touches a dollar amount (the invoices posture, FE edition).
 *
 * The current rate per band is the same pick the server's resolver
 * makes (latest effective_from <= today, null when none): duplicated
 * here as a PURE function over the fetched rows so the card can group
 * one fetch into per-band current + earlier without a per-band query.
 */

import { writeFetch } from './writeFetch';
import { useQuery } from '@tanstack/react-query';
import { ApiError } from '@/lib/api';
import { TRAVEL_RATE_BAND } from './notes';

export interface ClientRateRowWire {
  id: string;
  rate_band: string;
  /** numeric(12,2) as text — never a float. */
  rate: string;
  /** 'YYYY-MM-DD'. */
  effective_from: string;
  /** false = retired (0138): never prices new time; struck in history. */
  is_active: boolean;
  updated_at: string;
}

export function clientRatesKey(slug: string, clientId: string): readonly unknown[] {
  return ['client-rates', slug, clientId];
}

async function parseError(res: Response, label: string): Promise<ApiError> {
  let body: unknown;
  try {
    body = await res.json();
  } catch {
    body = await res.text().catch(() => null);
  }
  return new ApiError(res.status, body, `${label} → HTTP ${res.status}`);
}

export async function fetchClientRates(
  slug: string,
  clientId: string,
  signal?: AbortSignal,
): Promise<ClientRateRowWire[]> {
  const path =
    `/api/t/${encodeURIComponent(slug)}/v1/clients/${encodeURIComponent(clientId)}/rates`;
  const res = await fetch(path, {
    method: 'GET',
    headers: { Accept: 'application/json' },
    signal,
  });
  if (!res.ok) throw await parseError(res, `GET ${path}`);
  const payload = (await res.json()) as { rates: ClientRateRowWire[] };
  return payload.rates;
}

export async function putClientRate(
  slug: string,
  clientId: string,
  body: { rate_band: string; rate: string; effective_from: string },
): Promise<ClientRateRowWire[]> {
  const path =
    `/api/t/${encodeURIComponent(slug)}/v1/clients/${encodeURIComponent(clientId)}/rates`;
  const res = await writeFetch(path, {
    method: 'PUT',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    redirect: 'manual',
  });
  if (!res.ok) throw await parseError(res, `PUT ${path}`);
  const payload = (await res.json()) as { rates: ClientRateRowWire[] };
  return payload.rates;
}

/**
 * Retire (archived=true) / bring back (archived=false) one rate row —
 * the 0138 soft-archive flip. Echoes the refreshed history like the
 * PUT (the caller seeds a cache or refetches an aggregate).
 */
export async function patchClientRateArchived(
  slug: string,
  clientId: string,
  rateId: string,
  archived: boolean,
): Promise<ClientRateRowWire[]> {
  const path =
    `/api/t/${encodeURIComponent(slug)}/v1/clients/${encodeURIComponent(clientId)}` +
    `/rates/${encodeURIComponent(rateId)}/archive`;
  const res = await writeFetch(path, {
    method: 'PATCH',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify({ archived }),
    redirect: 'manual',
  });
  if (!res.ok) throw await parseError(res, `PATCH ${path}`);
  const payload = (await res.json()) as { rates: ClientRateRowWire[] };
  return payload.rates;
}

// ---------------------------------------------------------------------
// Admin > Client rates — the tenant-wide overview grid (aggregate GET)
// ---------------------------------------------------------------------

export interface ResolvedRateWire {
  /** The client_rates row that answered — the grid's row-level Archive
   *  affordance targets exactly this row. */
  id: string;
  /** numeric(12,2) as text — never a float. */
  rate: string;
  /** 'YYYY-MM-DD' — the row that answered. */
  effective_from: string;
}

export interface ClientRatesOverviewClientWire {
  id: string;
  display_name: string;
  /** band value -> the rate in effect on the overview date, or null
   *  ("No rate on file" — visible, never guessed around). */
  rates: Record<string, ResolvedRateWire | null>;
  /** band value -> the next rate to start after `on`, or null. Absent
   *  from an older server; read as none. */
  upcoming?: Record<string, ResolvedRateWire | null>;
}

export interface ClientRatesOverviewResponse {
  on: string;
  bands: { value: string; label: string; per_visit?: true }[];
  clients: ClientRatesOverviewClientWire[];
}

/** Admin-console cache namespace (the shared admin-review key pattern). */
export function clientRatesOverviewKey(slug: string, on: string): readonly unknown[] {
  return ['admin-review', 'client-rates', slug, on];
}

export async function fetchClientRatesOverview(
  slug: string,
  on: string,
  signal?: AbortSignal,
): Promise<ClientRatesOverviewResponse> {
  const path =
    `/api/t/${encodeURIComponent(slug)}/v1/client-rates?on=${encodeURIComponent(on)}`;
  const res = await fetch(path, {
    method: 'GET',
    headers: { Accept: 'application/json' },
    signal,
  });
  if (!res.ok) throw await parseError(res, `GET ${path}`);
  return (await res.json()) as ClientRatesOverviewResponse;
}

export interface UseClientRatesResult {
  rates: ClientRateRowWire[] | null;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  refetch: () => void;
}

/** `enabled: false` sends nothing: a seat without view_invoice never asks
 *  for a client's rates (the server refuses it anyway). */
export function useClientRates(
  slug: string,
  clientId: string,
  opts: { enabled?: boolean } = {},
): UseClientRatesResult {
  const query = useQuery<ClientRateRowWire[]>({
    queryKey: clientRatesKey(slug, clientId),
    queryFn: ({ signal }) => fetchClientRates(slug, clientId, signal),
    staleTime: 30_000,
    enabled: (opts.enabled ?? true) && slug !== '' && clientId !== '',
  });
  return {
    rates: query.data ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: () => {
      void query.refetch();
    },
  };
}

// ---------------------------------------------------------------------
// Pure presentation logic (unit-tested)
// ---------------------------------------------------------------------

/**
 * "$185.00" from the wire's "185.00" — string surgery only, no float.
 * Thousands separators on the integer part; the fraction is padded to
 * exactly 2 places (the server sends 2, but a bare "185" stays safe).
 */
export function formatMoney(rate: string): string {
  const [wholeRaw = '0', fracRaw = ''] = rate.split('.');
  const whole = wholeRaw.replace(/^0+(?=\d)/, '');
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `$${grouped}.${fracRaw.padEnd(2, '0').slice(0, 2)}`;
}

/** The card's per-band view of the one fetched list. */
export interface BandRates {
  /** The ACTIVE row in effect today (latest effective_from <= today),
   *  or null. An archived row is never the pick (0138: the server
   *  resolver's rule, presentation edition). */
  current: ClientRateRowWire | null;
  /** The next ACTIVE row to start (earliest effective_from > today), or
   *  null. A rate that starts later is a rate on file: it reads as
   *  starting later, never as earlier and never as no rate. */
  upcoming: ClientRateRowWire | null;
  /** Every other row for the band, newest first (the rate history:
   *  rows a newer one replaced, rows further ahead than `upcoming`, and
   *  archived rows, which render struck and dimmed). */
  others: ClientRateRowWire[];
}

/** Group the fetched rows for one band, pick today's rate and the next
 *  one to start: the server resolver's semantics, presentation edition
 *  (archived rows never answer). The one place the screens decide
 *  current, upcoming and history. */
export function bandRatesOf(
  rows: readonly ClientRateRowWire[],
  band: string,
  today: string,
): BandRates {
  const ofBand = rows
    .filter((r) => r.rate_band === band)
    .sort((a, b) => (a.effective_from < b.effective_from ? 1 : -1));
  let current: ClientRateRowWire | null = null;
  let upcoming: ClientRateRowWire | null = null;
  for (const row of ofBand) {
    if (!row.is_active) continue;
    // Newest first: the first active row on or before today is the pick;
    // the last active row after today seen before it is the next to start.
    if (row.effective_from <= today) {
      current = row;
      break;
    }
    upcoming = row;
  }
  return { current, upcoming, others: ofBand.filter((r) => r !== current && r !== upcoming) };
}

/** A rate band's unit as the screens print it after an amount. */
export function rateUnit(perVisit: boolean): string {
  return perVisit ? 'per visit' : '/ hour';
}

/**
 * The one example amount the rate screens show: 60.00 where the sentence
 * is about the Travel band, 120.00 for any other band or none.
 */
export function rateExample(band?: string): string {
  return band === TRAVEL_RATE_BAND ? '60.00' : '120.00';
}

/**
 * Dialog-input check: a positive amount, at most 2 decimals. Returns
 * the plain-words problem, or null when the value is good. (The server
 * re-validates — this is the polite first reading.) The malformed
 * sentence names the band's example amount.
 */
export function rateInputProblem(v: string, band?: string): string | null {
  const s = v.trim();
  if (s === '') return 'Enter an amount.';
  if (!/^\d{1,10}(\.\d{1,2})?$/.test(s)) {
    return `Enter the amount as dollars and cents, with no $ sign: ${rateExample(band)}.`;
  }
  if (/^0+(\.0{1,2})?$/.test(s)) return 'The amount has to be more than zero.';
  return null;
}

/** Canonical 2-decimal wire form ("185" -> "185.00") — string only. */
export function canonicalRateInput(v: string): string {
  const [whole = '0', frac = ''] = v.trim().split('.');
  return `${whole.replace(/^0+(?=\d)/, '')}.${frac.padEnd(2, '0').slice(0, 2)}`;
}
